/* eslint-disable react/no-unescaped-entities */
import { Link, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useLang } from "@/lib/i18n";
import LangSwitch from "@/components/LangSwitch";
import WhatsAppCTA from "@/components/WhatsAppCTA";
import WelcomeTour, { useTourAutoOpen, resetTour } from "@/components/WelcomeTour";
import ExpertConsult from "@/components/ExpertConsult";
import InstallAppButton from "@/components/InstallAppButton";
import { Button } from "@/components/ui/button";
import { LogOut, CreditCard, CheckCircle2, Clock, AlertCircle, Beef, Stethoscope, Heart, CalendarDays, Wheat, BarChart3, ArrowRight, PlayCircle, Gift, Zap, BookOpen } from "lucide-react";

function daysUntil(iso) {
  if (!iso) return null;
  try {
    const end = new Date(iso).getTime();
    const now = Date.now();
    const diff = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
    return diff;
  } catch {
    return null;
  }
}

/** Compact module tile — dense on mobile, roomier on desktop */
function ModuleCard({ to, testid, icon, iconBg, title, desc, openLabel }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className="group rounded-2xl sm:rounded-3xl bg-white border-2 border-[#E2E4DF] hover:border-[#E05A3D] active:border-[#E05A3D] p-3.5 sm:p-7 transition-all"
    >
      <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl sm:rounded-2xl grid place-items-center ${iconBg}`}>
        {icon}
      </div>
      <h3 className="mt-3 sm:mt-5 font-display text-sm sm:text-xl font-bold text-[#1C3A27] leading-tight">
        {title}
      </h3>
      <p className="hidden sm:block mt-2 text-sm text-[#5C6B5C]">{desc}</p>
      <p className="sm:hidden mt-1 text-[11px] text-[#5C6B5C] line-clamp-2 leading-snug">{desc}</p>
      <div className="mt-3 sm:mt-5 flex items-center gap-1.5 text-xs sm:text-sm font-semibold text-[#1C3A27] group-hover:text-[#E05A3D]">
        {openLabel} <ArrowRight size={14} className="sm:w-4 sm:h-4 group-hover:translate-x-1 transition" />
      </div>
    </Link>
  );
}

export default function ClientHome() {
  const { user, loading, logout } = useAuth();
  const { t } = useLang();
  const location = useLocation();
  const isActiveClient = !!user && user.role === "client" && user.status === "active" && !user.must_change_password;
  const [tourOpen, setTourOpen] = useTourAutoOpen(isActiveClient);

  if (loading) {
    return <div className="min-h-screen grid place-items-center bg-[#FAFAF7] text-[#5C6B5C]">{t.app.common.loading}</div>;
  }
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  if (user.must_change_password && location.pathname !== "/change-password") return <Navigate to="/change-password" replace />;
  if (user.role !== "client")
    return <Navigate to={user.role === "owner" ? "/dashboard/owner" : "/dashboard/trainer"} replace />;

  const isPending = user.status === "pending";
  const isSuspended = user.status === "suspended";
  const isTrialActive = user.is_trial && user.status === "active";
  const trialDaysLeft = isTrialActive ? daysUntil(user.trial_ends_at) : null;

  return (
    <div className="min-h-screen bg-[#FAFAF7] flex flex-col">
      <header className="bg-[#1C3A27] text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <span className="w-8 h-8 rounded-full bg-white grid place-items-center">
              <span className="w-3 h-3 rounded-full bg-[#E05A3D]" />
            </span>
            <span className="font-display text-lg font-bold">Mazra3ti</span>
          </Link>
          <div className="flex items-center gap-2">
            <LangSwitch />
            <Button onClick={logout} variant="ghost" size="sm" data-testid="client-logout" className="text-white/80 hover:text-white">
              <LogOut size={16} className="me-2" /> {t.app.common.logout}
            </Button>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-3xl w-full mx-auto px-4 sm:px-6 py-6 sm:py-12">
        <h1 className="font-display text-2xl sm:text-3xl md:text-4xl font-black text-[#1C3A27]">
          {t.app.client.hello} {user.name?.split(" ")[0]} 👋
        </h1>
        <p className="mt-1.5 sm:mt-2 text-sm sm:text-base text-[#5C6B5C]">{t.app.client.subtitle}</p>

        {isPending && (
          <div data-testid="client-pending-card" className="mt-8 rounded-3xl border-2 border-[#E05A3D] bg-[#E05A3D]/5 p-7 md:p-9">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-2xl bg-[#E05A3D] grid place-items-center shrink-0">
                <Clock size={22} className="text-white" />
              </div>
              <div className="flex-1">
                <h2 className="font-display text-2xl font-bold text-[#1C3A27]">{t.app.client.pending_title}</h2>
                <p className="mt-2 text-[#5C6B5C]">{t.app.client.pending_desc}</p>
                <div className="mt-5 flex flex-wrap gap-3">
                  <Link to="/pay">
                    <Button data-testid="cta-pay-online" className="h-12 px-5 bg-[#E05A3D] hover:bg-[#C84B31] text-white rounded-xl font-semibold">
                      <CreditCard size={18} className="me-2" />
                      {t.app.client.pay_now}
                    </Button>
                  </Link>
                  <WhatsAppCTA
                    data-testid="client-pending-whatsapp"
                    variant="outline"
                    message={t.app.whatsapp.pending_msg}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {isSuspended && (
          <div data-testid="client-suspended-card" className="mt-8 rounded-3xl border-2 border-amber-500 bg-amber-50 p-7 md:p-9">
            <div className="flex items-start gap-4">
              <AlertCircle size={26} className="text-amber-700 shrink-0" />
              <div className="flex-1">
                <h2 className="font-display text-xl font-bold text-amber-900">{t.app.client.suspended_title}</h2>
                <p className="mt-1 text-amber-800">{t.app.client.suspended_desc}</p>
                <div className="mt-4">
                  <WhatsAppCTA data-testid="client-suspended-whatsapp" />
                </div>
              </div>
            </div>
          </div>
        )}

        {!isPending && !isSuspended && (
          <>
            {isTrialActive && (
              <div
                data-testid="trial-banner"
                className="mt-8 rounded-3xl border-2 border-[#E05A3D] bg-gradient-to-br from-[#FFF5F1] to-[#FFEDE6] p-6 md:p-7"
              >
                <div className="flex flex-wrap items-start gap-5">
                  <div className="w-12 h-12 rounded-2xl bg-[#E05A3D] grid place-items-center shrink-0">
                    <Gift size={22} className="text-white" />
                  </div>
                  <div className="flex-1 min-w-[220px]">
                    <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] font-bold text-[#E05A3D]">
                      {t.app.trial.badge}
                    </div>
                    <h3 className="mt-2 font-display text-xl md:text-2xl font-bold text-[#1C3A27]">
                      {trialDaysLeft !== null && trialDaysLeft <= 0
                        ? t.app.trial.ends_today
                        : (trialDaysLeft === 1 ? t.app.trial.days_left_one : t.app.trial.days_left_many)
                            .replace("{n}", Math.max(0, trialDaysLeft ?? 0))}
                    </h3>
                    <p className="mt-1.5 text-sm text-[#5C6B5C]">{t.app.trial.banner_sub}</p>
                  </div>
                  <Link to="/pay" data-testid="trial-activate-btn">
                    <Button className="h-11 rounded-xl bg-[#1C3A27] hover:bg-[#142A1C] text-white font-semibold">
                      <CreditCard size={16} className="me-2" />
                      {t.app.trial.activate_btn}
                    </Button>
                  </Link>
                </div>
              </div>
            )}

            <div data-testid="client-active-card" className="mt-6 sm:mt-8 rounded-2xl sm:rounded-3xl bg-[#1C3A27] text-white p-4 sm:p-8">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 text-emerald-300">
                    <CheckCircle2 size={18} className="sm:w-[22px] sm:h-[22px]" />
                    <span className="text-[10px] sm:text-sm font-bold uppercase tracking-widest">{t.app.client.active_status}</span>
                  </div>
                  <h2 className="mt-1.5 sm:mt-3 font-display text-base sm:text-2xl font-bold leading-tight">
                    {t.app.client.active_plan
                      .replace("{plan}", user.plan)
                      .replace("{cycle}", user.billing_cycle === "annual" ? t.app.client.annual : t.app.client.monthly)}
                  </h2>
                  <p className="hidden sm:block mt-3 text-white/70">{t.app.client.modules_intro}</p>
                </div>
                <Button
                  onClick={() => { resetTour(); setTourOpen(true); }}
                  variant="ghost"
                  size="sm"
                  data-testid="open-tour-btn"
                  className="bg-white/10 hover:bg-white/15 text-white rounded-xl text-xs sm:text-sm h-8 sm:h-10 px-3"
                >
                  <PlayCircle size={14} className="me-1.5 sm:me-2" />
                  <span className="hidden xs:inline sm:inline">{t.app.tour.start_btn}</span>
                  <span className="xs:hidden sm:hidden">Tour</span>
                </Button>
              </div>
              <div className="hidden sm:flex mt-5 flex-wrap items-center gap-3">
                <InstallAppButton />
                <span className="text-xs text-white/60">Accédez à Mazra3ti hors-ligne, depuis votre écran d'accueil.</span>
              </div>
            </div>

            <Link
              to="/dashboard/client/quick-entry"
              data-testid="quick-entry-cta"
              className="mt-4 sm:mt-6 group flex items-center justify-between gap-3 p-3.5 sm:p-5 rounded-2xl sm:rounded-3xl bg-gradient-to-r from-[#E05A3D] to-[#C84B31] text-white hover:from-[#C84B31] hover:to-[#A33D26] shadow-xl transition-all hover:shadow-2xl"
            >
              <div className="flex items-center gap-3 sm:gap-4 min-w-0">
                <div className="w-11 h-11 sm:w-14 sm:h-14 rounded-xl sm:rounded-2xl bg-white/15 grid place-items-center flex-shrink-0">
                  <Zap size={22} className="sm:w-7 sm:h-7 text-white" fill="white" />
                </div>
                <div className="min-w-0">
                  <h3 className="font-display text-base sm:text-xl font-black tracking-tight">Saisie rapide ⚡</h3>
                  <p className="text-xs sm:text-sm text-white/85 line-clamp-1 sm:truncate">Poids, FAMACHA, vaccin, vermifuge… en 1 minute</p>
                </div>
              </div>
              <ArrowRight size={20} className="sm:w-[22px] sm:h-[22px] flex-shrink-0 group-hover:translate-x-1 transition" />
            </Link>

            <div className="mt-5 sm:mt-8 grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-5">
              <ModuleCard
                to="/dashboard/client/animals"
                testid="module-animals"
                icon={<Beef size={20} />}
                iconBg="bg-[#E05A3D]/15 text-[#E05A3D]"
                title={t.app.client.mod_animals_title}
                desc={t.app.client.mod_animals_desc}
                openLabel={t.app.client.open}
              />
              <ModuleCard
                to="/dashboard/client/health"
                testid="module-health"
                icon={<Stethoscope size={20} />}
                iconBg="bg-[#1C7A3C]/15 text-[#1C7A3C]"
                title={t.app.client.mod_health_title}
                desc={t.app.client.mod_health_desc}
                openLabel={t.app.client.open}
              />
              <ModuleCard
                to="/dashboard/client/reproduction"
                testid="module-reproduction"
                icon={<Heart size={20} />}
                iconBg="bg-pink-100 text-pink-700"
                title={t.app.repro.mod_title}
                desc={t.app.repro.mod_desc}
                openLabel={t.app.client.open}
              />
              <ModuleCard
                to="/dashboard/client/calendar"
                testid="module-calendar"
                icon={<CalendarDays size={20} />}
                iconBg="bg-amber-100 text-amber-700"
                title={t.app.calendar.mod_title}
                desc={t.app.calendar.mod_desc}
                openLabel={t.app.client.open}
              />
              <ModuleCard
                to="/dashboard/client/nutrition"
                testid="module-nutrition"
                icon={<Wheat size={20} />}
                iconBg="bg-yellow-100 text-yellow-800"
                title={t.app.nutrition.mod_title}
                desc={t.app.nutrition.mod_desc}
                openLabel={t.app.client.open}
              />
              <ModuleCard
                to="/dashboard/client/analytics"
                testid="module-analytics"
                icon={<BarChart3 size={20} />}
                iconBg="bg-purple-100 text-purple-700"
                title={t.app.analytics.mod_title}
                desc={t.app.analytics.mod_desc}
                openLabel={t.app.client.open}
              />
              <ModuleCard
                to="/dashboard/best-practices"
                testid="module-best-practices"
                icon={<BookOpen size={20} />}
                iconBg="bg-[#E05A3D]/15 text-[#E05A3D]"
                title="Bonnes pratiques"
                desc="Fiches illustrées santé, agnelage, secours…"
                openLabel="Consulter"
              />
            </div>

            {/* Install app CTA on mobile (hidden on desktop where it's in the active card) */}
            <div className="sm:hidden mt-4">
              <InstallAppButton />
            </div>

            {/* Expert consultation (Pro only) */}
            <ExpertConsult />
          </>
        )}
      </main>

      <WelcomeTour open={tourOpen} onClose={() => setTourOpen(false)} />
    </div>
  );
}
